import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpService } from '../http/http.service';
import { ApiResponse } from '@nebula-shared/domain';
import { PageResponse } from './crud.types';

@Injectable({ providedIn: 'root' })
export class BaseCrudService<T, CreatePayload = T, UpdatePayload = T> {
  protected http = inject(HttpService);
  protected baseUrl = '';

  protected setBaseUrl(url: string): void {
    this.baseUrl = url;
  }

  /**
   * Obtener un registro por ID (solo content)
   * GET /{baseUrl}/get?id={id}
   */
  getById(id: number | string): Observable<T> {
    return this.http.getContent<T>(`${this.baseUrl}/get`, { id: String(id) });
  }
  /**
   * Obtener un registro por ID con el estandar de page response (solo content)
   * GET /{baseUrl}/get-record?id={id}
   *  * @param id - The unique identifier of the record.
   * @returns An Observable containing the requested record.
   */
  getRecordById(id: number | string): Observable<T> {
    return this.http.getContent<T>(`${this.baseUrl}/get-record`, { id: String(id) });
  }

  /**
   * Obtener un registro único sin ID (solo content)
   * GET /{baseUrl}/get
   */
  getSingle(): Observable<T> {
    return this.http.getContent<T>(`${this.baseUrl}/get`);
  }

  /**
   * Obtener todos los registros (solo content)
   * GET /{baseUrl}/read
   */
  getAll(): Observable<T[]> {
    return this.http.getContent<T[]>(`${this.baseUrl}/read`);
  }
  /**
   * Obtener todos los registros paginados (solo content)
   * GET /{baseUrl}/page
   */
  getAllWithParams(filters: Record<string, string>): Observable<PageResponse<T>> {
    return this.http
      .get<PageResponse<T>>(`${this.baseUrl}/index`, filters)
      .pipe(map((response) => response.response.content));
  }

  /**
   * Obtener todos con filtros (solo content)
   * GET /{baseUrl}/read?filtro=valor
   */
  getAllWithFilters(filters: Record<string, string>): Observable<T[]> {
    return this.http.getContent<T[]>(`${this.baseUrl}/read`, filters);
  }

  /**
   * Crear un nuevo registro
   * POST /{baseUrl}/create
   */
  create(payload: CreatePayload): Observable<T> {
    return this.http
      .post<T>(`${this.baseUrl}/create`, payload)
      .pipe(map((response) => response.response.content));
  }

  /**
   * Actualizar un registro por ID
   * PUT /{baseUrl}/update?id={id}
   */
  update(id: number | string, payload: UpdatePayload): Observable<T> {
    return this.http
      .put<T>(`${this.baseUrl}/update?id=${id}`, payload)
      .pipe(map((response) => response.response.content));
  }

  /**
   * Actualizar un registro único sin ID
   * PUT /{baseUrl}/update
   */
  updateSingle(payload: UpdatePayload): Observable<T> {
    return this.http
      .put<T>(`${this.baseUrl}/update`, payload)
      .pipe(map((response) => response.response.content));
  }

  /**
   * Eliminar un registro por ID
   * DELETE /{baseUrl}/delete?id={id}
   */
  delete(id: number | string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/delete?id=${id}`).pipe(map(() => void 0));
  }

  /**
   * Eliminar un registro único sin ID
   * DELETE /{baseUrl}/delete
   */
  deleteSingle(): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/delete`).pipe(map(() => void 0));
  }

  /**
   * Paginación estándar (solo content)
   * POST /{baseUrl}/page
   */
  getPage(page = 0, size = 10, filters?: Record<string, any>): Observable<any> {
    const payload = { page, size, ...filters };
    return this.http
      .post<any>(`${this.baseUrl}/page`, payload)
      .pipe(map((response) => response.response));
  }

  /**
   * Paginación inmutable (solo content)
   * POST /{baseUrl}/page-response
   */
  getPageResponse(page = 0, size = 10, filters?: Record<string, any>): Observable<any> {
    const payload = { page, size, ...filters };
    return this.http
      .post<any>(`${this.baseUrl}/page-response`, payload)
      .pipe(map((response) => response.response));
  }

  /**
   * Obtener un registro por ID (respuesta completa con metadata)
   * GET /{baseUrl}/get?id={id}
   */
  getByIdRaw(id: number | string): Observable<ApiResponse<T>> {
    return this.http.get<T>(`${this.baseUrl}/get`, { id: String(id) });
  }

  /**
   * Obtener un registro único sin ID (respuesta completa con metadata)
   * GET /{baseUrl}/get
   */
  getSingleRaw(): Observable<ApiResponse<T>> {
    return this.http.get<T>(`${this.baseUrl}/get`);
  }

  /**
   * Obtener todos los registros (respuesta completa con metadata)
   * GET /{baseUrl}/read
   */
  getAllRaw(): Observable<ApiResponse<T[]>> {
    return this.http.get<T[]>(`${this.baseUrl}/read`);
  }

  /**
   * Obtener todos con filtros (respuesta completa con metadata)
   * GET /{baseUrl}/read?filtro=valor
   */
  getAllWithFiltersRaw(filters: Record<string, string>): Observable<ApiResponse<T[]>> {
    return this.http.get<T[]>(`${this.baseUrl}/read`, filters);
  }

  /**
   * Crear un nuevo registro (respuesta completa con metadata)
   * POST /{baseUrl}/create
   */
  createRaw(payload: CreatePayload): Observable<ApiResponse<T>> {
    return this.http.post<T>(`${this.baseUrl}/create`, payload);
  }

  /**
   * Actualizar un registro por ID (respuesta completa con metadata)
   * PUT /{baseUrl}/update?id={id}
   */
  updateRaw(id: number | string, payload: UpdatePayload): Observable<ApiResponse<T>> {
    return this.http.put<T>(`${this.baseUrl}/update?id=${id}`, payload);
  }

  /**
   * Actualizar un registro único sin ID (respuesta completa con metadata)
   * PUT /{baseUrl}/update
   */
  updateSingleRaw(payload: UpdatePayload): Observable<ApiResponse<T>> {
    return this.http.put<T>(`${this.baseUrl}/update`, payload);
  }

  /**
   * Eliminar un registro por ID (respuesta completa con metadata)
   * DELETE /{baseUrl}/delete?id={id}
   */
  deleteRaw(id: number | string): Observable<ApiResponse<void>> {
    return this.http.delete<void>(`${this.baseUrl}/delete?id=${id}`);
  }

  /**
   * Eliminar un registro único sin ID (respuesta completa con metadata)
   * DELETE /{baseUrl}/delete
   */
  deleteSingleRaw(): Observable<ApiResponse<void>> {
    return this.http.delete<void>(`${this.baseUrl}/delete`);
  }

  /**
   * Paginación estándar (respuesta completa con metadata)
   * POST /{baseUrl}/page
   */
  getPageRaw(page = 0, size = 10, filters?: Record<string, any>): Observable<ApiResponse<any>> {
    const payload = { page, size, ...filters };
    return this.http.post<any>(`${this.baseUrl}/page`, payload);
  }

  /**
   * Paginación inmutable (respuesta completa con metadata)
   * POST /{baseUrl}/page-response
   */
  getPageResponseRaw(
    page = 0,
    size = 10,
    filters?: Record<string, any>,
  ): Observable<ApiResponse<any>> {
    const payload = { page, size, ...filters };
    return this.http.post<any>(`${this.baseUrl}/page-response`, payload);
  }

  /**
   * Health check (respuesta completa)
   * GET /{baseUrl}/
   */
  healthCheckRaw(): Observable<ApiResponse<any>> {
    return this.http.get<any>(`${this.baseUrl}/`);
  }

  healthCheckRawBase(): Observable<ApiResponse<any>> {
    return this.http.get<any>(`${this.baseUrl}`);
  }
}
